import React, { useEffect, useState } from "react";
import { Table, Input, Button, Spin, message } from "antd";
import moment from "moment";
import axios from "axios";
import { useSelector } from "react-redux";
import { addZeroes, getLatestUpdated } from "../Components/CommonFunction";
import { useDispatch } from "react-redux";
import { setMetalData } from "../Redux/Actions/MetalDataActions";

const METAL_MASTER = [
  { label: "Gold 18 KT", key: "Gold 18", item_group: "GOLD", karat: "18" },
  { label: "Gold 22 KT", key: "Gold 22", item_group: "GOLD", karat: "22" },
  { label: "Gold 24 KT", key: "Gold 24", item_group: "GOLD", karat: "24" },
  { label: "Silver Article", key: "Silver 0", item_group: "SILVER", karat: "ARTICLE" },
  { label: "Silver Bar", key: "silver 100", item_group: "SILVER", karat: "BAR" },
];

const normalizeProduct = (name = "") =>
  name.replace(" K", "").replace(" KT", "").trim();

const GSSRateConfigTable = () => {
  const reduxData = useSelector((state) => state.MetalDataReducer.data);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    loadInitialRates();
  }, [reduxData]);

  const loadInitialRates = async () => {
    try {
      setLoading(true);

      const rateMap = {};
      reduxData.forEach((r) => {
        rateMap[normalizeProduct(r.product_name)] = r;
      });

      const finalData = METAL_MASTER.map((m) => {
        const match = rateMap[m.key];

        return {
          ...m,
          sale_amount: match?.sale_amount ?? null,
          purchase_amount: match?.purchase_amount ?? null,
          updated: match?.updated ?? null,
          isEditing: false,
          editSale: match?.sale_amount ?? "",
          editPurchase: match?.purchase_amount ?? "",
        };
      });

      setData(finalData);
    } catch (err) {
      message.error("Failed to load GSS rates");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (record) => {
    setData((prev) =>
      prev.map((row) =>
        row.key === record.key ? { ...row, isEditing: true } : row
      )
    );
  };

  const cancelEdit = (record) => {
    setData((prev) =>
      prev.map((row) =>
        row.key === record.key
          ? {
              ...row,
              isEditing: false,
              editSale: row.sale_amount,
              editPurchase: row.purchase_amount,
            }
          : row
      )
    );
  };

  const saveRow = async (record) => {
    try {
      setSavingKey(record.key);

      const payload = [
        {
          product_name: record.key,
          item_group: record.item_group,
          karat: record.karat,
          unit: "gm",
          sale_rate: Number(record.editSale),
          purchase_rate: Number(record.editPurchase),
          updated_at: moment().format("YYYY-MM-DD HH:mm:ss"),
        },
      ];

      await axios.post("/gss/config", payload);

      const res = await axios.get("/gss/goldrate");

      dispatch(setMetalData(res.data));

      message.success("Rate updated successfully");
      loadInitialRates();
    } catch (err) {
      message.error("Failed to save rate");
    } finally {
      setSavingKey(null);
    }
  };

  const columns = [
    {
      title: "Metal Type",
      dataIndex: "label",
      align: "center",
    },
    {
      title: "Sale Per gram",
      align: "center",
      render: (_, record) =>
        record.isEditing ? (
          <Input
            style={{ width: 150, textAlign: "center" }}
            value={record.editSale}
            onChange={(e) =>
              setData((prev) =>
                prev.map((row) =>
                  row.key === record.key
                    ? { ...row, editSale: e.target.value }
                    : row
                )
              )
            }
          />
        ) : record.sale_amount == null ? (
          "-"
        ) : (
          `₹${addZeroes(record.sale_amount)}`
        ),
    },
    {
      title: "Purchase Per gram",
      align: "center",
      render: (_, record) =>
        record.isEditing ? (
          <Input
            style={{ width: 150, textAlign: "center" }}
            value={record.editPurchase}
            onChange={(e) =>
              setData((prev) =>
                prev.map((row) =>
                  row.key === record.key
                    ? { ...row, editPurchase: e.target.value }
                    : row
                )
              )
            }
          />
        ) : record.purchase_amount == null ? (
          "-"
        ) : (
          `₹${addZeroes(record.purchase_amount)}`
        ),
    },
    {
      title: "Action",
      align: "center",
      width: 200,
      render: (_, record) =>
        record.isEditing ? (
          <>
            <Button
              type="primary"
              size="small"
              onClick={() => saveRow(record)}
              loading={savingKey === record.key}
            >
              Save
            </Button>
            <Button
              danger
              size="small"
              style={{ marginLeft: 8 }}
              onClick={() => cancelEdit(record)}
            >
              Cancel
            </Button>
          </>
        ) : (
            <Button style={{ backgroundColor: "#6b0f1a", color: "#fff", border: "none", fontWeight: 500, }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#8b1c2d")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#6b0f1a")} onClick={() => startEdit(record)}>
              Edit
            </Button>
        ),
    },
  ];
  const latestUpdated = getLatestUpdated(data);

  return (
    <div>
      <div style={{ float: "right", marginBottom: 10, fontWeight: "bold" }}>
        Updated On:{" "}
        {latestUpdated
          ? moment(latestUpdated.updated).format("YYYY-MM-DD hh:mm:ss A")
          : "-"}
      </div>

      {loading ? (
        <Spin size="large" />
      ) : (
        <Table
          columns={columns}
          dataSource={data}
          rowKey="key"
          pagination={false}
          bordered
          size="small"
        />
      )}
    </div>
  );
};

export default GSSRateConfigTable;